import { useState, useEffect, useCallback, useRef } from 'react';
import './Jobs.scss';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Loader from '../../components/Loader';
import DefaultLogo from './default.png';

const Library = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const fetchedRef = useRef(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationValues, setLocationValues] = useState([]);
  const [isLocationsExpanded, setIsLocationsExpanded] = useState(false);

  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [industryValues, setIndustryValues] = useState([]);
  const [isIndustryExpanded, setIsIndustryExpanded] = useState(false);

  const [selectedFunction, setSelectedFunction] = useState([]);
  const [functionValues, setFunctionValues] = useState([]);
  const [isFunctionExpanded, setIsFunctionExpanded] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState([]);
  const [companyValues, setCompanyValues] = useState([]);
  const [isCompanyExpanded, setIsCompanyExpanded] = useState(false);

  const getJobs = async (key = null) => {
    try {
      const baseUrl = 'https://jobs.thepioneers.ai/job/organization/nextdeavor/description';
      const url = key ? `${baseUrl}?last_key=${key}` : baseUrl;

      const response = await fetch(url);
      const res = await response.json();

      setIsLoading(false);

      setData(prev => [...prev, ...res.Items]);
      setFilteredRows(prev => [...prev, ...res.Items]);

      if (res.last_key) {
        await getJobs(res.last_key);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getJobs();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [data, searchValue, selectedLocations, selectedCompany, selectedFunction, selectedIndustry]);

  useEffect(() => {
    const locationOptions = [];
    const industryOptions = [];
    const functionOptions = [];
    const companyOptions = [];

    data?.forEach(item => {
      if (!locationOptions.includes(item.location) && !!item.location && item.location != ' ')
        locationOptions.push(item.location);
      if (!industryOptions.includes(item.company?.industry) && !!item.company?.industry && item.company?.industry != ' ')
        industryOptions.push(item.company?.industry);
      if (!functionOptions.includes(item.job_function) && !!item.job_function && item.job_function != ' ')
        functionOptions.push(item.job_function);
      if (!companyOptions.includes(item.company?.title) && !!item.company?.title && item.company?.title != ' ')
        companyOptions.push(item.company?.title);
    });

    setLocationValues(locationOptions);
    setIndustryValues(industryOptions);
    setFunctionValues(functionOptions);
    setCompanyValues(companyOptions);
  }, [data]);

  const handleWindowSizeChange = () => {
    setIsMobile(window.innerWidth <= 1200);
    setIsMobileFiltersOpen(false);
  };

  useEffect(() => {
    setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleWindowSizeChange);

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  const handleSearch = useCallback(() => {
    const nameParts = searchValue.toLowerCase().split(' ').filter(Boolean);

    let newResults = data.filter(item =>
      nameParts.every(
        part =>
          item.title?.toLowerCase()?.includes(part) ||
          item.summary?.toLowerCase()?.includes(part) ||
          item.anon_cv_markdown?.toLowerCase()?.includes(part),
      ),
    );

    if (selectedLocations?.length) {
      newResults = newResults.filter(item => selectedLocations.includes(item.location));
    }
    if (selectedCompany?.length) {
      newResults = newResults.filter(item => selectedCompany.includes(item.company?.title));
    }
    if (selectedFunction?.length) {
      newResults = newResults.filter(item => selectedFunction.includes(item.job_function));
    }
    if (selectedIndustry?.length) {
      newResults = newResults.filter(item => selectedIndustry.includes(item.company?.industry));
    }

    // newResults?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    setFilteredRows(newResults);
  }, [data, searchValue, selectedLocations, selectedCompany, selectedFunction, selectedIndustry]);

  const handleChangeCategory = (name, value) => {
    if (name === 'location') {
      if (selectedLocations.includes(value)) setSelectedLocations(selectedLocations.filter(item => item !== value));
      else setSelectedLocations([...selectedLocations, value]);
    }
    if (name === 'industry') {
      if (selectedIndustry.includes(value)) setSelectedIndustry(selectedIndustry.filter(item => item !== value));
      else setSelectedIndustry([...selectedIndustry, value]);
    }
    if (name === 'function') {
      if (selectedFunction.includes(value)) setSelectedFunction(selectedFunction.filter(item => item !== value));
      else setSelectedFunction([...selectedFunction, value]);
    }
    if (name === 'company') {
      if (selectedCompany.includes(value)) setSelectedCompany(selectedCompany.filter(item => item !== value));
      else setSelectedCompany([...selectedCompany, value]);
    }
  };

  const renderFilters = () => {
    return (
      <div className='jobFiltersList'>
        <div className='jobFilterItem'>
          <span className='jobFilterItemTitle'>Location</span>
          <div className='jobFilterItemList'>
            {(isLocationsExpanded ? locationValues : locationValues.slice(0, 5)).map(item => (
              <label className='jobFilterItemValue'>
                <input
                  type='checkbox'
                  checked={selectedLocations.includes(item)}
                  className='jobFilterItemCheckbox'
                  onChange={() => handleChangeCategory('location', item)}
                />
                <span className='jobFilterItemCheckboxMark'></span> {item}
              </label>
            ))}
            {locationValues.length > 5 && (
              <div>
                {isLocationsExpanded ? (
                  <span className='jobsFilterMore' onClick={() => setIsLocationsExpanded(false)}>
                    Hide{' '}
                    <span style={{ transform: 'rotate(-180deg)', height: '20px' }}>
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <path
                          d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                          fill='#3ABC83'
                        />
                      </svg>
                    </span>
                  </span>
                ) : (
                  <span className='jobsFilterMore' onClick={() => setIsLocationsExpanded(true)}>
                    {locationValues.length - 5} more{' '}
                    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                      <path
                        d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                        fill='#3ABC83'
                      />
                    </svg>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='jobFilterItem'>
          <span className='jobFilterItemTitle'>Company</span>
          <div className='jobFilterItemList'>
            {(isCompanyExpanded ? companyValues : companyValues.slice(0, 5)).map(item => (
              <label className='jobFilterItemValue'>
                <input
                  type='checkbox'
                  checked={selectedCompany.includes(item)}
                  className='jobFilterItemCheckbox'
                  onChange={() => handleChangeCategory('company', item)}
                />
                <span className='jobFilterItemCheckboxMark'></span> {item}
              </label>
            ))}
            {companyValues.length > 5 && (
              <div>
                {isCompanyExpanded ? (
                  <span className='jobsFilterMore' onClick={() => setIsCompanyExpanded(false)}>
                    Hide{' '}
                    <span style={{ transform: 'rotate(-180deg)', height: '20px' }}>
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <path
                          d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                          fill='#3ABC83'
                        />
                      </svg>
                    </span>
                  </span>
                ) : (
                  <span className='jobsFilterMore' onClick={() => setIsCompanyExpanded(true)}>
                    {companyValues.length - 5} more{' '}
                    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                      <path
                        d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                        fill='#3ABC83'
                      />
                    </svg>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='jobFilterItem'>
          <span className='jobFilterItemTitle'>Industry</span>
          <div className='jobFilterItemList'>
            {(isIndustryExpanded ? industryValues : industryValues.slice(0, 5)).map(item => (
              <label className='jobFilterItemValue'>
                <input
                  type='checkbox'
                  checked={selectedIndustry.includes(item)}
                  className='jobFilterItemCheckbox'
                  onChange={() => handleChangeCategory('industry', item)}
                />
                <span className='jobFilterItemCheckboxMark'></span> {item}
              </label>
            ))}
            {industryValues.length > 5 && (
              <div>
                {isIndustryExpanded ? (
                  <span className='jobsFilterMore' onClick={() => setIsIndustryExpanded(false)}>
                    Hide{' '}
                    <span style={{ transform: 'rotate(-180deg)', height: '20px' }}>
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <path
                          d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                          fill='#3ABC83'
                        />
                      </svg>
                    </span>
                  </span>
                ) : (
                  <span className='jobsFilterMore' onClick={() => setIsIndustryExpanded(true)}>
                    {industryValues.length - 5} more{' '}
                    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                      <path
                        d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                        fill='#3ABC83'
                      />
                    </svg>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='jobFilterItem'>
          <span className='jobFilterItemTitle'>Job Function</span>
          <div className='jobFilterItemList'>
            {(isFunctionExpanded ? functionValues : functionValues.slice(0, 5)).map(item => (
              <label className='jobFilterItemValue'>
                <input
                  type='checkbox'
                  checked={selectedFunction.includes(item)}
                  className='jobFilterItemCheckbox'
                  onChange={() => handleChangeCategory('function', item)}
                />
                <span className='jobFilterItemCheckboxMark'></span> {item}
              </label>
            ))}
            {functionValues.length > 5 && (
              <div>
                {isFunctionExpanded ? (
                  <span className='jobsFilterMore' onClick={() => setIsFunctionExpanded(false)}>
                    Hide{' '}
                    <span style={{ transform: 'rotate(-180deg)', height: '20px' }}>
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <path
                          d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                          fill='#3ABC83'
                        />
                      </svg>
                    </span>
                  </span>
                ) : (
                  <span className='jobsFilterMore' onClick={() => setIsFunctionExpanded(true)}>
                    {functionValues.length - 5} more{' '}
                    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                      <path
                        d='M10.6272 12.9596L15.3355 8.24292C15.4136 8.16545 15.4756 8.07328 15.5179 7.97174C15.5602 7.87019 15.582 7.76126 15.582 7.65125C15.582 7.54124 15.5602 7.43232 15.5179 7.33077C15.4756 7.22922 15.4136 7.13706 15.3355 7.05959C15.1794 6.90438 14.9682 6.81726 14.748 6.81726C14.5279 6.81726 14.3167 6.90438 14.1605 7.05959L9.99387 11.1846L5.86887 7.05959C5.71273 6.90438 5.50152 6.81726 5.28137 6.81726C5.06121 6.81726 4.85 6.90438 4.69387 7.05959C4.61513 7.13677 4.55248 7.2288 4.50957 7.33037C4.46666 7.43193 4.44432 7.541 4.44387 7.65125C4.44432 7.76151 4.46666 7.87058 4.50957 7.97214C4.55248 8.0737 4.61513 8.16574 4.69387 8.24292L9.4022 12.9596C9.48023 13.0442 9.57494 13.1117 9.68036 13.1579C9.78578 13.204 9.89961 13.2279 10.0147 13.2279C10.1298 13.2279 10.2436 13.204 10.349 13.1579C10.4545 13.1117 10.5492 13.0442 10.6272 12.9596Z'
                        fill='#3ABC83'
                      />
                    </svg>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='page'>
      <Header />
      <div className='jobsPage'>
        {!isLoading && <div className='jobsLeft'>{renderFilters()}</div>}
        {!isLoading ? (
          <div className='jobsContent'>
            <div className='jobsContentHeader'>
              <div className='jobsMobileFiltersButton' onClick={() => setIsMobileFiltersOpen(true)}>
                Filters{' '}
                <svg width={24} height={24} viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M11 18H13C13.55 18 14 17.55 14 17C14 16.45 13.55 16 13 16H11C10.45 16 10 16.45 10 17C10 17.55 10.45 18 11 18ZM3 7C3 7.55 3.45 8 4 8H20C20.55 8 21 7.55 21 7C21 6.45 20.55 6 20 6H4C3.45 6 3 6.45 3 7ZM7 13H17C17.55 13 18 12.55 18 12C18 11.45 17.55 11 17 11H7C6.45 11 6 11.45 6 12C6 12.55 6.45 13 7 13Z'
                    fill='#3ABC83'
                  />
                </svg>
              </div>
              <span className='jobsContentHeaderTitle'>{filteredRows.length} Jobs</span>
            </div>
            <div className='jobsList'>
              {filteredRows.map(item => (
                <div className='jobsCard' onClick={() => navigate(`/jobs/${item.id}`)}>
                  <div className='jobsCardHeader'>
                    <span className='jobsCardLogo'>
                      <img src={item.company?.logo || DefaultLogo} />
                    </span>
                    <span className='jobsCardTitle'>{item.title}</span>
                    <span className='jobsCardCompany'>
                      {item.company?.title} • {item.location}
                    </span>
                  </div>
                  <div className='jobsCardContent'>
                    {item.company?.description || 'Exclusive confidential search — details shared with qualified applicants.'}
                  </div>
                  <div className='jobsCardFooter'>
                    <span className='jobsCardTime'>
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <path
                          d='M15.8337 5.41665H13.3337V4.58331C13.3337 3.92027 13.0703 3.28439 12.6014 2.81555C12.1326 2.34671 11.4967 2.08331 10.8337 2.08331H9.16699C8.50395 2.08331 7.86807 2.34671 7.39923 2.81555C6.93038 3.28439 6.66699 3.92027 6.66699 4.58331V5.41665H4.16699C3.50395 5.41665 2.86807 5.68004 2.39923 6.14888C1.93038 6.61772 1.66699 7.2536 1.66699 7.91665V15.4166C1.66699 16.0797 1.93038 16.7156 2.39923 17.1844C2.86807 17.6533 3.50395 17.9166 4.16699 17.9166H15.8337C16.4967 17.9166 17.1326 17.6533 17.6014 17.1844C18.0703 16.7156 18.3337 16.0797 18.3337 15.4166V7.91665C18.3337 7.2536 18.0703 6.61772 17.6014 6.14888C17.1326 5.68004 16.4967 5.41665 15.8337 5.41665ZM8.33366 4.58331C8.33366 4.3623 8.42146 4.15034 8.57774 3.99406C8.73402 3.83778 8.94598 3.74998 9.16699 3.74998H10.8337C11.0547 3.74998 11.2666 3.83778 11.4229 3.99406C11.5792 4.15034 11.667 4.3623 11.667 4.58331V5.41665H8.33366V4.58331ZM16.667 15.4166C16.667 15.6377 16.5792 15.8496 16.4229 16.0059C16.2666 16.1622 16.0547 16.25 15.8337 16.25H4.16699C3.94598 16.25 3.73402 16.1622 3.57774 16.0059C3.42146 15.8496 3.33366 15.6377 3.33366 15.4166V10.8333C5.45554 11.6751 7.71757 12.1077 10.0003 12.1083C12.283 12.1069 14.5449 11.6743 16.667 10.8333V15.4166ZM16.667 9.00831C14.5657 9.93146 12.2955 10.4081 10.0003 10.4081C7.70515 10.4081 5.435 9.93146 3.33366 9.00831V7.91665C3.33366 7.69563 3.42146 7.48367 3.57774 7.32739C3.73402 7.17111 3.94598 7.08331 4.16699 7.08331H15.8337C16.0547 7.08331 16.2666 7.17111 16.4229 7.32739C16.5792 7.48367 16.667 7.69563 16.667 7.91665V9.00831Z'
                          fill='#384766'
                        />
                      </svg>
                      {item.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className='loader'>{<Loader />}</div>
        )}
      </div>
      {isMobileFiltersOpen && (
        <div className='mobileFilters'>
          {renderFilters()}
          <span className='mobileFiltersClose' onClick={() => setIsMobileFiltersOpen(false)}>
            <svg width={20} height={20} viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M8.41 6.99994L12.71 2.70994C12.8983 2.52164 13.0041 2.26624 13.0041 1.99994C13.0041 1.73364 12.8983 1.47825 12.71 1.28994C12.5217 1.10164 12.2663 0.99585 12 0.99585C11.7337 0.99585 11.4783 1.10164 11.29 1.28994L7 5.58994L2.71 1.28994C2.5217 1.10164 2.2663 0.99585 2 0.99585C1.7337 0.99585 1.4783 1.10164 1.29 1.28994C1.1017 1.47825 0.995908 1.73364 0.995908 1.99994C0.995908 2.26624 1.1017 2.52164 1.29 2.70994L5.59 6.99994L1.29 11.2899C1.19627 11.3829 1.12188 11.4935 1.07111 11.6154C1.02034 11.7372 0.994202 11.8679 0.994202 11.9999C0.994202 12.132 1.02034 12.2627 1.07111 12.3845C1.12188 12.5064 1.19627 12.617 1.29 12.7099C1.38296 12.8037 1.49356 12.8781 1.61542 12.9288C1.73728 12.9796 1.86799 13.0057 2 13.0057C2.13201 13.0057 2.26272 12.9796 2.38458 12.9288C2.50644 12.8781 2.61704 12.8037 2.71 12.7099L7 8.40994L11.29 12.7099C11.383 12.8037 11.4936 12.8781 11.6154 12.9288C11.7373 12.9796 11.868 13.0057 12 13.0057C12.132 13.0057 12.2627 12.9796 12.3846 12.9288C12.5064 12.8781 12.617 12.8037 12.71 12.7099C12.8037 12.617 12.8781 12.5064 12.9289 12.3845C12.9797 12.2627 13.0058 12.132 13.0058 11.9999C13.0058 11.8679 12.9797 11.7372 12.9289 11.6154C12.8781 11.4935 12.8037 11.3829 12.71 11.2899L8.41 6.99994Z'
                fill='#384766'
              />
            </svg>
          </span>
          <div className='mobileFiltersApply' onClick={() => setIsMobileFiltersOpen(false)}>
            Apply
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Library;
